/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCommonData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxData
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxFindInStoreViewProps } from '@msdyn365-commerce-modules/buybox';
import AgeGate from '../../../components/age-gate';
import { useCallback, useEffect, useState } from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

const BuyboxView: React.FC<IBuyboxViewProps &  IBuyboxProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        unitOfMeasure,
        productComparisonButton
    } = props;

    const [isAgeGateModalOpen, setIsAgeGateModalOpen] = useState<boolean>(false);
    const [showContent, setShowContent] = useState<boolean>(false);

    const ageGateCookieName = (props.context.app.config.ageGateCookieName as string) || 'ageGateCookie';
    const cookies = props.context.request.cookies;
    const ageGateCookie = cookies.get(ageGateCookieName) as Msdyn365.ICookieValue<{}> | undefined;
    const isAgeVerifiedByCookie = ageGateCookie && ageGateCookie.value === 'true';
    const productData = ((props as IBuyboxViewProps).data as IBuyboxCommonData).product;
    const ageRestrictedCategories = ['liquor', 'alcohol', 'wine', 'spirit'];
    const requiresAgeGate = productData.result?.ExtensionProperties?.some(extProperty => {
        const key = extProperty.Key?.toLowerCase();
        const stringValue = extProperty.Value?.StringValue?.toLowerCase();

        if (key?.includes('level2categoryname')) {
            return ageRestrictedCategories.some(category => stringValue?.includes(category));
        }
        return false;
    });

    useEffect(() => {
        // This effect runs on mount and whenever `requiresAgeGate` or `isAgeVerifiedByCookie` changes.
        if (requiresAgeGate) {
            // If the product requires an age gate:
            if (!isAgeVerifiedByCookie) {
                // And the user is NOT verified by cookie, then open the age gate and hide content.
                setIsAgeGateModalOpen(true);
                setShowContent(false);
            } else {
                // If required, but user IS verified, then hide age gate and show content.
                setIsAgeGateModalOpen(false);
                setShowContent(true);
            }
        } else {
            // If the product does NOT require an age gate, then show content immediately.
            setIsAgeGateModalOpen(false); // Ensure gate is not open
            setShowContent(true);          // Show content
        }
    }, [requiresAgeGate, isAgeVerifiedByCookie]);

    const handleAgeGateClose = useCallback(() => {
        setIsAgeGateModalOpen(false);
        setShowContent(true);
    }, []);

    return (
        <React.Fragment>
            {isAgeGateModalOpen && (
                <AgeGate
                    externalIsOpen={isAgeGateModalOpen} // Controls visibility from parent
                    onCloseExternallyTriggered={handleAgeGateClose} // Callback when AgeGate is dismissed
                    context={props.context}
                    triggeredFrom='buybox'
                />
            )}

        {showContent ? (
            <Module {...ModuleProps}>
                <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
                <Node {...ProductInfoContainerProps}>
                    {title}
                    {price}
                    {unitOfMeasure}
                    {props.bulkPurchaseLink}
                    {description}
                    {rating}
                    {configure && _renderConfigure(configure)}
                    {keyInPrice && _renderKeyInPrice(keyInPrice)}
                    {quantity && _renderQuantity(quantity)}
                    {inventoryLabel}
                    {_renderProductSpecification(props.slots && props.slots.productSpecification)}
                    {addToCart && _renderAddToCart(addToCart)}
                    {findInStore && _renderFindInStore(findInStore)}
                    {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)}
                    {addToWishlist && _renderAddToWishlist(addToWishlist)}
                    {_renderSocialShare(props.slots && props.slots.socialShare)}
                    {productComparisonButton}
                    {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                    {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                </Node>
            </Module>) : null}
        </React.Fragment>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderProductSpecification = (productSpecification: React.ReactNode[]): JSX.Element | undefined => {
    if (!productSpecification || productSpecification.length === 0) {
        return undefined;
    }

    return <>{productSpecification[0]}</>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
